import {
    DataFilterOperator,
    DataTypeOperator,
    type Filter,
    type FilterGroup,
    type FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";

const filterOperatorValues = new Set<string>(Object.values(DataFilterOperator));
const ruleOperatorValues = new Set<string>(Object.values(DataTypeOperator));

const stringValues = (value: unknown) => {
    if (!Array.isArray(value)) return undefined;
    return value
        .map(item => typeof item === 'string' ? item : item === null ? undefined : null)
        .filter((item): item is string | undefined => item !== null);
};

const numberValues = (value: unknown, integer: boolean) => {
    if (!Array.isArray(value)) return undefined;
    return value
        .map(item => typeof item === 'number' && Number.isFinite(item)
            ? integer ? Math.round(item) : item
            : item === null ? undefined : null)
        .filter((item): item is number | undefined => item !== null);
};

const timestampValues = (value: unknown) => {
    if (!Array.isArray(value)) return undefined;
    return value
        .map(item => {
            if (item === null) return undefined;
            if (typeof item !== 'string' && !(item instanceof Date)) return null;
            const date = new Date(item);
            return Number.isNaN(date.getTime()) ? null : date;
        })
        .filter((item): item is Date | undefined => item !== null);
};

export const normalizeFilterRule = (value: unknown): FilterRule | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

    const source = value as Record<string, unknown>;
    if (typeof source.field !== 'string' || !source.field.trim()) return undefined;
    if (typeof source.operator !== 'string' || !ruleOperatorValues.has(source.operator)) return undefined;

    const rule: FilterRule = {
        field: source.field,
        operator: source.operator as DataTypeOperator
    };
    const text = stringValues(source.textValues);
    const longs = numberValues(source.longValues, true);
    const doubles = numberValues(source.doubleValues, false);
    const localDates = stringValues(source.localDateValues);
    const timestamps = timestampValues(source.timestampValues);

    if (text) rule.textValues = text;
    if (longs) rule.longValues = longs;
    if (doubles) rule.doubleValues = doubles;
    if (localDates) rule.localDateValues = localDates;
    if (timestamps) rule.timestampValues = timestamps;
    if (typeof source.isExclusive === 'boolean') rule.isExclusive = source.isExclusive;
    if (typeof source.isDeactivated === 'boolean') rule.isDeactivated = source.isDeactivated;

    return rule;
};

export const normalizeFilterRules = (value: unknown): FilterRule[] => (
    Array.isArray(value)
        ? value.map(normalizeFilterRule).filter((rule): rule is FilterRule => !!rule)
        : []
);

const normalizeFilterOperator = (value: unknown) => (
    typeof value === 'string' && filterOperatorValues.has(value)
        ? value as DataFilterOperator
        : undefined
);

const normalizeFilterGroup = (value: unknown, depth: number): FilterGroup | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || depth > 20) return undefined;

    const source = value as Record<string, unknown>;
    const group: FilterGroup = {};
    const operator = normalizeFilterOperator(source.operator);

    if (source.operator !== undefined && !operator) return undefined;
    if (source.filterRules !== undefined && !Array.isArray(source.filterRules)) return undefined;
    if (source.subGroups !== undefined && !Array.isArray(source.subGroups)) return undefined;
    if (operator) group.operator = operator;
    if (Array.isArray(source.filterRules)) {
        const filterRules = normalizeFilterRules(source.filterRules);
        if (filterRules.length !== source.filterRules.length) return undefined;
        group.filterRules = filterRules;
    }
    if (Array.isArray(source.subGroups)) {
        const subGroups = source.subGroups
            .map(item => normalizeFilterGroup(item, depth + 1))
            .filter((item): item is FilterGroup => !!item);
        if (subGroups.length !== source.subGroups.length) return undefined;
        group.subGroups = subGroups;
    }

    return group;
};

export const normalizeFilter = (value: unknown): Filter | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

    const source = value as Record<string, unknown>;
    const filter: Filter = {};
    const operator = normalizeFilterOperator(source.operator);

    if (source.operator !== undefined && !operator) return undefined;
    if (source.filterGroups !== undefined && !Array.isArray(source.filterGroups)) return undefined;
    if (operator) filter.operator = operator;
    if (Array.isArray(source.filterGroups)) {
        const filterGroups = source.filterGroups
            .map(item => normalizeFilterGroup(item, 0))
            .filter((item): item is FilterGroup => !!item);
        if (filterGroups.length !== source.filterGroups.length) return undefined;
        filter.filterGroups = filterGroups;
    }

    return filter;
};
